'use strict';
const DIRECTORY_QUERY_KEYS = Object.freeze(['q', 'category', 'city', 'sort', 'openOnly', 'tagIds', 'matchMode', 'page']);
function directoryQueryPlan(params) {
  const query = new URLSearchParams();
  let invalid = false;
  for (const [key, value] of params) {
    if (DIRECTORY_QUERY_KEYS.includes(key)) { query.append(key, value); if (value.includes('\0')) invalid = true; }
    else if (key.includes('[') && DIRECTORY_QUERY_KEYS.includes(key.split('[')[0])) { query.append(key, value); invalid = true; }
  }
  if (DIRECTORY_QUERY_KEYS.some(key => params.getAll(key).length > 1)) invalid = true;
  return { query, invalid };
}

function normalizePublicUrl(value) {
  if (typeof value !== 'string' || value.length > 2048 || !value.startsWith('/') || value.startsWith('//') || /[\\\r\n\0#]/.test(value)) return null;
  const url = new URL(value, 'https://public.invalid');
  if (url.origin !== 'https://public.invalid') return null;
  return `${url.pathname}${url.search}`;
}

// Only this finite plan can issue anonymous API subrequests. Caller input is
// encoded into path segments or allowlisted query values, never an authority.
function publicDataPlan(value) {
  const normalized = normalizePublicUrl(value);
  if (!normalized) return null;
  const url = new URL(normalized, 'https://public.invalid');
  url.pathname = url.pathname.replace(/\/+$/, '') || '/';
  if (['preview', 'embed', 'editor', 'previewTier'].some(key => url.searchParams.has(key))) return null;
  try { decodeURIComponent(url.pathname); } catch { return null; }
  const parts = url.pathname.split('/').filter(Boolean);
  if (parts[0] === 'listings' && [2, 3].includes(parts.length) && parts.at(-1) !== 'claim') {
    return { kind: 'listing', path: `/api/listings/${parts.slice(1).map(value => encodeURIComponent(decodeURIComponent(value))).join('/')}` };
  }
  if (url.pathname === '/listings') {
    const { query, invalid } = directoryQueryPlan(url.searchParams);
    return { kind: 'directory', path: `/api/directory/businesses?${query}`, ...(invalid ? { invalidQuery: true } : {}) };
  }
  if (parts[0] === 'vote' && parts.length === 2) return { kind: 'nominee', path: `/api/community-program-nominees/${encodeURIComponent(decodeURIComponent(parts[1]))}/public` };
  if (parts[0] === 'events' && parts.length === 2) return { kind: 'event', path: `/api/events/${encodeURIComponent(decodeURIComponent(parts[1]))}` };
  if (parts[0] === 'blog' && parts.length === 2 && parts[1] !== 'category') return { kind: 'article', path: `/api/content/publications/${encodeURIComponent(decodeURIComponent(parts[1]))}` };
  if (parts[0] === 'experts' && parts.length === 2) return { kind: 'expert', path: `/api/experts/public/profiles/${encodeURIComponent(decodeURIComponent(parts[1]))}` };
  if (url.pathname === '/') return { kind: 'home', path: '/api/public/homepage/listings' };
  if (url.pathname === '/events') return { kind: 'events', path: `/api/events?from=${encodeURIComponent(new Date().toISOString())}` };
  if (url.pathname === '/experts') return { kind: 'experts', path: '/api/experts/public/profiles?limit=12&offset=0' };
  if (url.pathname === '/blog' || (parts[0] === 'blog' && parts[1] === 'category' && parts.length === 3)) {
    const query = new URLSearchParams();
    if (parts[1] === 'category') query.set('categorySlug', decodeURIComponent(parts[2]));
    for (const key of ['search', 'page', 'format']) if (url.searchParams.has(key)) query.set(key, url.searchParams.get(key));
    const invalidQuery = ['search', 'page', 'format'].some(key => url.searchParams.getAll(key).length > 1) || [...url.searchParams.keys()].some(key => /^(search|page|format)\[/.test(key));
    return { kind: 'articles', invalidQuery, path: `/api/content/publications${query.size ? `?${query}` : ''}` };
  }
  if (url.pathname === '/offers' || (parts[0] === 'offers' && parts[1] === 'category' && parts.length === 3)) return { kind: 'offers', path: `/api/offers${parts[1] === 'category' ? `?categorySlug=${encodeURIComponent(decodeURIComponent(parts[2]))}` : ''}` };
  if (parts[0] === 'offers' && parts.length === 2) return { kind: 'offer', path: `/api/offers/${encodeURIComponent(decodeURIComponent(parts[1]))}` };
  if (parts[0] === 'community-programs' && (parts.length === 2 || (parts.length === 4 && parts[2] === 'category'))) {
    const query = new URLSearchParams();
    if (parts.length === 4) query.set('categoryId', decodeURIComponent(parts[3]));
    if (url.searchParams.has('page')) query.set('page', url.searchParams.get('page'));
    return { kind: parts.length === 2 ? 'program' : 'programCategory', path: `/api/community-programs/${encodeURIComponent(decodeURIComponent(parts[1]))}/public${query.size ? `?${query}` : ''}` };
  }
  const mailers = { '/advertise/mailer': 'landing-page', '/advertise/mailer/tracking': 'tracking-page', '/advertise/mailer/spotlight-deal': 'spotlight-deal-page' };
  if (mailers[url.pathname]) return { kind: 'mailer', path: `/api/public/mailers/${mailers[url.pathname]}` };
  if (['/privacy-policy', '/terms-of-use', '/cookie-policy'].includes(url.pathname)) return { kind: 'legal', path: `/api/legal-pages${url.pathname}` };
  if (url.pathname === '/advertise/spotlight') return { kind: 'pricing', path: '/api/public/commerce/listing-plans' };
  if (['/about', '/contact'].includes(url.pathname)) return { kind: 'static', path: null };

  return null;
}

module.exports = { DIRECTORY_QUERY_KEYS, directoryQueryPlan, normalizePublicUrl, publicDataPlan };
